import React, { useEffect, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Container,
  HStack,
  Image,
  Progress,
  Radio,
  RadioGroup,
  Stat,
  StatArrow,
  StatHelpText,
  StatLabel,
  StatNumber,
  Text,
  VStack,
} from "@chakra-ui/react";
import Loader from "./Loader";
import Error from "./Error";
import axios from "axios";
import { server } from "../index";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import Chart from "./Chart";

const CoinDetails = () => {
  const [coin, setCoin] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [currency, setCurrency] = useState("inr");
  const [days, setDays] = useState("24h");
  const [chartArray, setChartArray] = useState([]);
  const params = useParams();

  const currencySymbol =
    currency === "inr" ? "₹" : currency === "eur" ? "€" : "$";

  const btns = ["24h", "7d", "14d", "30d", "60d", "200d", "1y", "max"];

  const switchChartStats = (key) => {
    switch (key) {
      case "24h":
        setDays("24h");
        setLoading(true);
        break;
      case "7d":
        setDays("7d");
        setLoading(true);
        break;
      // ... (other cases)
      default:
        setDays("24h");
        setLoading(true);
        break;
    }
  };

  useEffect(() => {
    const fetchCoin = async () => {
      try {
        const { data } = await axios.get(`${server}/coins/${params.id}`);
        const { data: chartData } = await axios.get(
          `${server}/coins/${params.id}/market_chart?vs_currency=${currency}&days=${days}`
        );

        setCoin(data);
        setChartArray(chartData.prices);
        setLoading(false);
      } catch (error) {
        setError(true);
        setLoading(false);
      }
    };
    fetchCoin();
  }, [params.id, currency, days]);

  if (error) return <Error message={"Error while fetching coin"} />;

  return (
    <Container maxW={"container.xl"} bgColor={"blackAlpha.900"} p={8}>
      {loading ? (
        <Loader />
      ) : (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Box bg="whiteAlpha.800" p={4} borderRadius="md">
            <Box w={"full"} borderWidth={1}>
              <Chart arr={chartArray} currency={currencySymbol} days={days} />
            </Box>

            <HStack p={4} wrap={"wrap"}>
              {btns.map((i) => (
                <Button
                  key={i}
                  onClick={() => switchChartStats(i)}
                  _hover={{ bg: "gray.200" }}
                  _active={{ bg: "gray.300" }}
                  size="sm"
                >
                  {i}
                </Button>
              ))}
            </HStack>

            <RadioGroup value={currency} onChange={setCurrency} p={8}>
              <HStack spacing={4}>
                <Radio value="inr">₹ INR</Radio>
                <Radio value="usd">$ USD</Radio>
                <Radio value="eur">€ EUR</Radio>
              </HStack>
            </RadioGroup>

            <VStack p={16} alignItems={"flex-start"} spacing={4}>
              <Text fontSize={"small"} alignSelf={"center"} opacity={0.7}>
                Last updated on{" "}
                {new Date(coin.market_data.last_updated).toLocaleString()}
              </Text>

              <Image
                src={coin.image.large}
                w={16}
                h={16}
                objectFit={"contain"}
              />

              <Stat>
                <StatLabel>{coin.name}</StatLabel>
                <StatNumber>
                  {currencySymbol}
                  {coin.market_data.current_price[currency]}
                </StatNumber>
                <StatHelpText>
                  <StatArrow
                    type={
                      coin.market_data.price_change_percentage_24h > 0
                        ? "increase"
                        : "decrease"
                    }
                  />
                  {coin.market_data.price_change_percentage_24h}%
                </StatHelpText>
              </Stat>

              <Badge
                fontSize={"larger"}
                bgColor={"blackAlpha.900"}
                color={"white"}
              >{`#${coin.market_cap_rank}`}</Badge>

              <CustomBar
                high={`${currencySymbol}${coin.market_data.high_24h[currency]}`}
                low={`${currencySymbol}${coin.market_data.low_24h[currency]}`}
              />

              <Box w={"full"} p={4}>
                <Item
                  title={"Max Supply"}
                  value={coin.market_data.max_supply}
                />
                <Item
                  title={"Circulating Supply"}
                  value={coin.market_data.circulating_supply}
                />
                <Item
                  title={"Market Cap"}
                  value={`${currencySymbol}${coin.market_data.market_cap[currency]}`}
                />
                <Item
                  title={"All Time Low"}
                  value={`${currencySymbol}${coin.market_data.atl[currency]}`}
                />
                <Item
                  title={"All Time High"}
                  value={`${currencySymbol}${coin.market_data.ath[currency]}`}
                />
              </Box>
            </VStack>
          </Box>
        </motion.div>
      )}
    </Container>
  );
};

const CustomBar = ({ high, low }) => (
  <VStack w={"full"}>
    <Progress value={50} colorScheme="teal" w={"full"} />
    <HStack w={"full"} justifyContent={"space-between"}>
      <Badge colorScheme="red" children={low} />
      <Text fontSize={"sm"}>24H Range</Text>
      <Badge colorScheme="green" children={high} />
    </HStack>
  </VStack>
);

const Item = ({ title, value }) => (
  <HStack w={"full"} justifyContent={"space-between"} my={4}>
    <Text fontFamily={"Bebas Neue"} letterSpacing={"widest"}>
      {title}
    </Text>
    <Text>{value}</Text>
  </HStack>
);

export default CoinDetails;
